import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import Code, { CodeText } from '../components/Code';
import DecisionPanel from '../components/DecisionPanel';
import ProvenanceChip from '../components/ProvenanceChip';
import ExplainThis from '../components/ExplainThis';

// Each signal's primary response lives in one engine; the drift packet's 'Explain this' grounds in
// that engine's context record (the explain endpoint floors to the on-page facts if it cannot match).
const SIGNAL_ENGINE: Record<string, number> = {
  'S-01': 2, 'S-02': 1, 'S-03': 5, 'S-04': 5, 'S-05': 2, 'S-06': 5,
  'S-07': 5, 'S-08': 1, 'S-09': 4, 'S-10': 1, 'S-11': 7, 'S-12': 3,
};

export default function DriftPacket() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [aiWaitCost, setAiWaitCost] = useState<string | null>(null);
  const [aiWaitLoading, setAiWaitLoading] = useState(false);

  const drift = useQuery({ queryKey: ['drift', id], queryFn: () => api.drift(id), enabled: !!id });
  // Fetch all signals so we can show "related signals" threatening the same objective
  const signals = useQuery({ queryKey: ['signals'], queryFn: api.signals });

  async function explainWithGemini() {
    setAiSummaryLoading(true);
    setAiSummary(null);
    try {
      const res = await fetch(`/api/drift/${id}/summarize`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-demo-passcode': import.meta.env.VITE_DEMO_PASSCODE ?? 'arizona-c2-demo' },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAiSummary(`Gemini unavailable: ${(j as { detail?: string; error?: string }).detail ?? (j as { error?: string }).error ?? res.statusText}`);
      } else {
        setAiSummary((j as { summary: string }).summary);
      }
    } catch {
      setAiSummary('Gemini request failed.');
    } finally {
      setAiSummaryLoading(false);
    }
  }

  async function explainWaitCost() {
    setAiWaitLoading(true);
    setAiWaitCost(null);
    try {
      const j = await api.driftWaitCost(id);
      setAiWaitCost(j.summary);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Gemini request failed.';
      setAiWaitCost(`Gemini unavailable: ${msg}`);
    } finally {
      setAiWaitLoading(false);
    }
  }

  if (drift.isLoading) return <div className="small faint">Loading drift packet…</div>;
  if (drift.isError) {
    return (
      <div>
        <div className="ph">Strategy Drift Packet</div>
        <div className="h2">Packet not found</div>
        <p className="lead">No drift packet exists with that ID. It may have been resolved or never created.</p>
        <Link to="/drift" className="btn primary">← Back to open packets</Link>
      </div>
    );
  }
  const d = drift.data!;

  // Related signals: same objective_id, different signal_id
  const related = (signals.data ?? []).filter(
    (s) => s.objective_id === d.objective.id && s.id !== d.signal.id,
  );
  const relatedBreached = related.filter((s) => s.tripwire_breached);
  const relatedWatching = related.filter((s) => !s.tripwire_breached && s.current_value > 0.3);

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div className="ph">Strategy Drift Packet</div>
          <div className="h2">{d.signal.name}</div>
        </div>
        <Link to="/drift" className="btn ghost" style={{ marginTop: 8 }}>← All packets</Link>
      </div>

      <div className="grid2">
        {/* Left — what it touches */}
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--red)' }} />What it touches</div>
          <Field l="Assumption breached" v={<><Code id={d.assumption.id} kind="assumption" compact codeOnly /> {d.assumption.statement}</>} />
          <Field l="Objective threatened" v={d.objective.name} />
          <Field l="Money Map node" v={<Code id={d.money_node.id} kind="stage" compact />} />
          <Field l="Owner to decide" v={d.owner} />
          <Field l="Early-warning threshold" v={d.signal.tripwire} />
          {/* Source / evidence line */}
          <div className="field" style={{ borderBottom: 'none' }}>
            <div className="field-label">Source · evidence</div>
            <div className="field-value">{d.signal.source_registry}</div>
          </div>
        </div>

        {/* Right — economic consequence */}
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--gold)' }} />Economic consequence (if no action)</div>
          <div className="kpi">
            <div className="num" style={{ color: 'var(--red)' }}>
              {d.revenue_at_risk_usd_m ? `−$${d.revenue_at_risk_usd_m[0]}–${d.revenue_at_risk_usd_m[1]}M` : '—'}
            </div>
            <div className="lab">revenue at risk if unaddressed</div>
            <div style={{ marginTop: 6 }}><ProvenanceChip basis="modeled on a representative cost stack" /></div>
          </div>
          <div className="divider" />
          <div style={{ fontSize: 14, marginBottom: 12, lineHeight: 1.5 }}>{d.economic_consequence}</div>
          <div className="small" style={{ marginBottom: 8 }}>Simulations to run:</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {d.simulations.map((s, i) => (
              <span key={i} className="tag blue">{s}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Candidate options — full table with name + impact + tier + status */}
      <div className="panel" style={{ marginTop: 14 }}>
        <div className="qhead">
          <span className="qdot" style={{ background: 'var(--amber)' }} />
          Candidate options · {d.candidate_options.length} from the catalogue
        </div>
        <table>
          <thead>
            <tr>
              <th style={{ width: 70 }}>ID</th>
              <th>Option</th>
              <th style={{ width: 90, textAlign: 'right' }}>Impact</th>
              <th style={{ width: 100, textAlign: 'right' }}>Readiness</th>
              <th style={{ width: 120 }}>Tier</th>
              <th style={{ width: 130 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {d.candidate_options.map((o) => {
              const blocked = o.status === 'BLOCKED';
              return (
                <tr key={o.id} style={blocked ? { opacity: 0.65 } : undefined}>
                  <td><Code id={o.id} kind="intervention" compact codeOnly /></td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{o.name}</div>
                    <div className="small" style={{ marginTop: 2 }}>{o.value_chain_anchor} · {o.ai_value_type}</div>
                  </td>
                  <td className="mono" style={{ textAlign: 'right', fontWeight: 600, color: blocked ? 'var(--faint)' : 'var(--gold)' }}>
                    {o.impact?.toFixed(2) ?? '—'}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>{o.readiness?.toFixed(2) ?? '—'}</td>
                  <td>
                    <span className={`tag ${blocked ? 'muted' : 'amber'}`}>{o.tier ?? '—'}</span>
                  </td>
                  <td>
                    {blocked ? (
                      <span className="blockedchip">Off the table · <CodeText text={o.veto_hits?.join(', ') ?? ''} compact /></span>
                    ) : (
                      <span className="tag olive">Pass ✓</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Related signals threatening the same objective */}
      {(relatedBreached.length > 0 || relatedWatching.length > 0) && (
        <div className="panel" style={{ marginTop: 14 }}>
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--teal)' }} />
            Also threatening {d.objective.name} · {relatedBreached.length} breached · {relatedWatching.length} watching
          </div>
          <div className="small" style={{ marginBottom: 10 }}>
            Other signals in the registry that map to the same objective. The C2 sees the whole picture,
            not one signal at a time.
          </div>
          {[...relatedBreached, ...relatedWatching].slice(0, 5).map((s) => (
            <div key={s.id} className="row" style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--line-soft)' }}>
              <div className="row">
                <span className={s.tripwire_breached ? 'tag red' : 'tag amber'}><Code id={s.id} kind="signal" compact /></span>
                <span style={{ fontSize: 14 }}>{s.name}</span>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <span className="small" style={{ color: 'var(--faint)' }}><Code id={s.assumption_id} kind="assumption" compact codeOnly /></span>
                <span className="mono small">{s.tripwire_breached ? 'Breached' : `${s.current_value.toFixed(2)} / 1.0`}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gemini AI panels — two-up grid */}
      <div className="grid2" style={{ marginTop: 14 }}>
        <div className="panel">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="qhead" style={{ margin: 0 }}>
              <span className="qdot" style={{ background: 'var(--blue)' }} />
              Plain-English summary
            </div>
            <button type="button" className="btn ghost" onClick={explainWithGemini} disabled={aiSummaryLoading}>
              {aiSummaryLoading ? 'Asking…' : aiSummary ? 'Refresh' : '✨ Explain'}
            </button>
          </div>
          {aiSummary ? (
            <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.55, color: 'var(--ink)' }}>{aiSummary}</div>
          ) : (
            <div className="small" style={{ marginTop: 12 }}>Click Explain to get a 2-3 sentence CTO-readable summary.</div>
          )}
        </div>
        <div className="panel">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="qhead" style={{ margin: 0 }}>
              <span className="qdot" style={{ background: 'var(--red)' }} />
              What if we wait?
            </div>
            <button type="button" className="btn ghost" onClick={explainWaitCost} disabled={aiWaitLoading}>
              {aiWaitLoading ? 'Asking…' : aiWaitCost ? 'Refresh' : '⏳ Estimate'}
            </button>
          </div>
          {aiWaitCost ? (
            <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.55, color: 'var(--ink)' }}>{aiWaitCost}</div>
          ) : (
            <div className="small" style={{ marginTop: 12 }}>Click Estimate to see the cost of choosing "monitor" for 4-8 weeks.</div>
          )}
        </div>
      </div>

      {/* 'Explain this in plain language' on the drift brief (spec 11.5; marked AI aid, the packet stays of record). */}
      <ExplainThis
        engine_id={SIGNAL_ENGINE[d.signal.id] ?? 6}
        recommendation={(() => {
          const top = d.candidate_options.find((o) => o.status !== 'BLOCKED');
          return top ? `Recommended response: ${top.name}` : d.economic_consequence;
        })()}
        evidence={d.revenue_at_risk_usd_m
          ? [{ label: 'Revenue at risk', value: `$${d.revenue_at_risk_usd_m[0]}–${d.revenue_at_risk_usd_m[1]}M`, provenance: 'modeled estimate' }]
          : []}
        tradeoffs={[d.economic_consequence]}
        constraints={d.candidate_options.filter((o) => o.status === 'BLOCKED').map((o) => `Off the table: ${o.name}`)}
      />

      {/* Decide from here — the one decision model */}
      <DecisionPanel
        owner={d.owner}
        commit={(disposition, envelope) => api.setDisposition(id, disposition, d.owner ?? user?.email ?? 'Operator', envelope)}
        onSimulate={() => navigate('/wargame')}
      />
    </div>
  );
}

function Field({ l, v }: { l: string; v: ReactNode }) {
  return (
    <div className="field">
      <div className="field-label">{l}</div>
      <div className="field-value">{v}</div>
    </div>
  );
}
